import { FC } from 'react';
import { Modal, FileInput, SelectInput } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { useImportVouchersXls } from 'store/admin-card-vouchers';
import { Stack } from '@mui/material';
import { useAdminCard } from 'store/admin-cards';
import { formatCurrency } from 'utils/format';
import { FileImportErrorsModal } from 'components/modals';
import { VouchersImportWarningsModal } from '..';

interface Values {
  files: File[];
  cardPrice: string;
}

interface Props extends ModalProps {
  cardId: string;
  cardPrice?: string;
}

export const ImportVoucherCodesModal: FC<Props> = ({
  cardId,
  cardPrice,
  closeModal,
}) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();

  const { control, handleSubmit } = useForm<Values>({
    values: { files: [], cardPrice: cardPrice ?? '' },
    mode: 'onChange',
  });

  const { data: card } = useAdminCard(cardId);

  const { mutate: importVouchers, status } = useImportVouchersXls();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (!card) return;

    importVouchers(
      {
        cardId,
        cardPrice: values.cardPrice,
        cardCurrency: card.faceValueCurrency,
        file: values.files[0],
      },
      {
        onSuccess: ({ data }) => {
          closeModal({ action: 'CLOSE' });

          if (!data.success) {
            openModal({
              component: FileImportErrorsModal,
              size: 'lg',
              props: {
                title: t('shop.vouchers.actions.upload_codes'),
                onBack: () =>
                  openModal({
                    component: ImportVoucherCodesModal,
                    size: 'lg',
                    props: { cardId, cardPrice },
                  }),
                errors: data.errorMessages.map((error) => ({
                  row: error.row,
                  field: error.column,
                  message: error.message,
                })),
              },
            });
          } else if (data.existingVouchers.length) {
            openModal({
              component: VouchersImportWarningsModal,
              props: {
                duplicates: data.existingVouchers,
              },
            });
          } else {
            predefinedToasts.success.added();
          }
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('shop.vouchers.actions.upload_codes')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('actions.import'),
            disabled: status === 'loading',
            type: 'submit',
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="cardPrice"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('marketplace.ordersTablePrice')}
                name={name}
                value={value}
                onChange={onChange}
                options={
                  card?.prices.map((price) => ({
                    label: formatCurrency(
                      i18n.language,
                      price.faceValue,
                      card.faceValueCurrency,
                    ),
                    value: price.faceValue,
                  })) ?? []
                }
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="files"
            control={control}
            render={({ field: { name, onChange, ref } }) => (
              <FileInput
                name={name}
                onChange={onChange}
                accept={['xls', 'xlsx']}
                maxFiles={1}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
